export const fiveHoursInMs = 5 * 60 * 60 * 1000;

export const parseDate = (str: string): Date | null => {
  const [dd, mm, yyyy] = str.split("-");
  const d = new Date(`${yyyy}-${mm}-${dd}`);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * 
 * @param isoString 
 * @param offset 
 * @returns true if the isoString Date *minus* the offset is in the past
 */
export const isPastDate = (isoString: string, offset: Date = new Date(0)): boolean => {
  const inputDate = new Date(isoString);
  const now = new Date();

  // Invalid date check
  if (isNaN(inputDate.getTime())) {
    return false;
  }

  return (inputDate.getTime() - offset.getTime()) < now.getTime();
}

/**
 * 
 * @param isoString 
 * @param offset 
 * @returns true if the isoString Date *minus* the offset is in the future
 */
export const isFutureDate = (isoString: string, offset: Date = new Date(0)): boolean => {
  return !isPastDate(isoString, offset);
}

export const getTimeDifferenceInMs = (startIso: string, endIso: string): number => {
  const startDate = new Date(startIso);
  const endDate = new Date(endIso);

  // Invalid date check
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return 0;
  }
  const diffInMs = endDate.getTime() - startDate.getTime();
  return diffInMs; // Convert milliseconds to hours
}

export const getTodayString = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

export const getLastMonday = (date = new Date()) => {
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const diff = (day === 0 ? 6 : day - 1); // How many days to subtract to get to last Monday
    const lastMonday = new Date(date);
    lastMonday.setDate(date.getDate() - diff);
    lastMonday.setHours(0, 0, 0, 0); // Optional: reset time to midnight
    return lastMonday;
  }

export const getNextSunday = (date = new Date()) => {
  const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const diff = (day === 0 ? 0 : 7 - day); // Days to add to reach next Sunday
  const nextSunday = new Date(date);
  nextSunday.setDate(date.getDate() + diff);
  nextSunday.setHours(23, 59, 0, 0); // Optional: reset time to midnight
  return nextSunday;
}

export const isCurrentWeek = (dateString = "") => {
  // Parse the provided day, or default to today
  const parsedDate = parseDate(dateString) ?? parseDate(getTodayString());
  const baseDate = parsedDate ?? new Date();
  // Calculate week start (Monday) and end (Sunday)
  const weekStart = getLastMonday(baseDate);
  const weekEnd = getNextSunday(baseDate);
  // Check if the current week is being displayed
  return new Date() >= weekStart && new Date() <= weekEnd;
}
  
export const formatDateForPageHeader = (v?: string | Date | null, withYear: boolean = false) => {
  const format: any = {
    month: 'short',
    day: '2-digit',
  }
  if (withYear) {
    format.year = "numeric"
  }
  return formatDate(v, format);
  };

export const formatDateForDayColumn = (v?: string | Date | null) => {
  return formatDate(v, {
    month: 'long',
    day: '2-digit',
    weekday: 'long',
  })
};

export const formatDateForEventItem = (v?: string | Date | null) => {
  return formatDate(v, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (v?: string | Date | null, format?: Intl.DateTimeFormatOptions) => {
    if (!v) return '';
    const d = typeof v === 'string' ? new Date(v) : v;
    if (!(d instanceof Date) || isNaN(d.getTime())) return String(v);
    const options: Intl.DateTimeFormatOptions = {
      hour12: false, // Force 24-hour format
      ...format
    };
    return d.toLocaleString(undefined, options);
}